// src/mySettings/settingComponent/planSetting/SelectedPlan.js
import React, { useState, useRef, useEffect } from 'react';
import './selectedPlan.css';

// FIREBASE
import { db, auth } from '../../../firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// PURCHASE MODAL
import PlanPurchasePage from '../../../myPlans/PlanPurchasePage';
import PaymentSetting from './paymentSetting';

// ICONS
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faRedo } from '@fortawesome/free-solid-svg-icons';

const PLAN_CONFIG = {
  free: {
    label: "Free Plan",
    subtitle: "Usage resets:",
    benefits: [
      "YouTube processing",
      "60 minutes of usage per month",
      "Chat with your video",
      "Prompts workspace",
      "SEO tool",
      "Video timestamps",
    ]
  },
  pro: {
    label: "Bumpups Pro",
    subtitle: "Usage resets:",
    benefits: [
      "Everything in Free",
      "Local video processing",
      "Up to 3h 30m per video",
      "Social media scheduler",
      "Multiple aspect ratios",
    ]
  }
};

const normalizePlan = (rawPlan) => {
  if (!rawPlan) return 'free';
  const p = rawPlan.toString().toLowerCase().trim();
  if (p === 'free plan' || p === 'free') return 'free';
  if (p === 'pro') return 'pro';
  return 'free';
};

const SelectedPlan = () => {
  const user = auth.currentUser;

  // billing portal callable
  const functions = getFunctions();
  const manageBillingCallable = httpsCallable(functions, 'manageBilling');

  // plan state + dates + cancel flag
  const [plan, setPlan] = useState(() => {
    const stored = localStorage.getItem(`currentPlan_${user?.uid}`);
    return normalizePlan(stored);
  });
  const [quotaCycleEnd, setQuotaCycleEnd] = useState(() => {
    const saved = localStorage.getItem(`quotaCycleEnd_${user?.uid}`);
    return saved ? new Date(saved) : null;
  });
  const [billingCycleEnd, setBillingCycleEnd] = useState(() => {
    const saved = localStorage.getItem(`billingCycleEnd_${user?.uid}`);
    return saved ? new Date(saved) : null;
  });
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(() => {
    return localStorage.getItem(`cancelAtPeriodEnd_${user?.uid}`) === 'true';
  });

  // dropdown + purchase-modal
  const [isOpen, setIsOpen] = useState(false);
  const [showPlanPopUp, setShowPlanPopUp] = useState(false);
  const menuRef = useRef(null);

  // fire PlanPurchasePage
  const handleUpgrade = () => {
    setShowPlanPopUp(true);
    setIsOpen(false);
  };

  // call the billing portal for renewing
  const handleRenew = async () => {
    setIsOpen(false);
    try {
      const { data } = await manageBillingCallable();
      window.location.href = data.url;
    } catch (err) {
      // console.error('Failed to open billing portal:', err);
    }
  };

  useEffect(() => {
    const planRef = doc(db, 'users', user.uid, 'subscriptionData', 'planDetails');

    const fetchAndListen = async () => {
      try {
        const snap = await getDoc(planRef);
        if (!snap.exists()) return;
        const data = snap.data();

        const newPlan = normalizePlan(data.currentPlan);
        setPlan(newPlan);
        localStorage.setItem(`currentPlan_${user.uid}`, newPlan);

        if (data.quotaCycleEnd?.toDate) {
          const d = data.quotaCycleEnd.toDate();
          setQuotaCycleEnd(d);
          localStorage.setItem(`quotaCycleEnd_${user.uid}`, d.toISOString());
        } else {
          setQuotaCycleEnd(null);
          localStorage.removeItem(`quotaCycleEnd_${user.uid}`);
        }

        if (data.billingCycleEnd?.toDate) {
          const d = data.billingCycleEnd.toDate();
          setBillingCycleEnd(d);
          localStorage.setItem(`billingCycleEnd_${user.uid}`, d.toISOString());
        } else {
          setBillingCycleEnd(null);
          localStorage.removeItem(`billingCycleEnd_${user.uid}`);
        }

        const cancelFlag = Boolean(data.cancelAtPeriodEnd);
        setCancelAtPeriodEnd(cancelFlag);
        localStorage.setItem(`cancelAtPeriodEnd_${user.uid}`, cancelFlag.toString());
      } catch (err) {
        // console.error('Error fetching plan details:', err);
      }
    };

    fetchAndListen();
    const unsubscribe = onSnapshot(planRef, snap => {
      if (!snap.exists()) return;
      const data = snap.data();

      const newPlan = normalizePlan(data.currentPlan);
      setPlan(newPlan);
      localStorage.setItem(`currentPlan_${user.uid}`, newPlan);

      if (data.quotaCycleEnd?.toDate) {
        const d = data.quotaCycleEnd.toDate();
        setQuotaCycleEnd(d);
        localStorage.setItem(`quotaCycleEnd_${user.uid}`, d.toISOString());
      } else {
        setQuotaCycleEnd(null);
        localStorage.removeItem(`quotaCycleEnd_${user.uid}`);
      }

      if (data.billingCycleEnd?.toDate) {
        const d = data.billingCycleEnd.toDate();
        setBillingCycleEnd(d);
        localStorage.setItem(`billingCycleEnd_${user.uid}`, d.toISOString());
      } else {
        setBillingCycleEnd(null);
        localStorage.removeItem(`billingCycleEnd_${user.uid}`);
      }

      const cancelFlag = Boolean(data.cancelAtPeriodEnd);
      setCancelAtPeriodEnd(cancelFlag);
      localStorage.setItem(`cancelAtPeriodEnd_${user.uid}`, cancelFlag.toString());
    });

    return () => unsubscribe();
  }, [user]);

  // click-outside closes dropdown
  useEffect(() => {
    const onClick = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const { label, subtitle, benefits } = PLAN_CONFIG[plan] || PLAN_CONFIG.free;

  const formattedQuotaEnd = quotaCycleEnd
    ? quotaCycleEnd.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';
  const formattedBillingEnd = billingCycleEnd
    ? billingCycleEnd.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : '—';

  return (
    <div className="account-settings-container">
      <div className="settings-row">
        <div className="settings-label plan-name">{label}</div>

        {/* always show Manage dropdown for both Free & Pro */}
        <div className="plan-settings-field" ref={menuRef}>
          <button
            type="button"
            className="ui-setting-button"
            onClick={() => setIsOpen(o => !o)}
            aria-expanded={isOpen}
          >
            Manage <span className="dropdown-arrow">▾</span>
          </button>

          {isOpen && (
            <ul className="plan-dropdown-menu">
              <li className="plan-dropdown-item">
                <button type="button" onClick={handleUpgrade}>
                  <FontAwesomeIcon icon={faRocket} style={{ marginRight: '8px' }} />
                  Upgrade Plan
                </button>
              </li>

              {plan === 'pro' && cancelAtPeriodEnd && (
                <li className="plan-dropdown-item">
                  <button type="button" onClick={handleRenew}>
                    <FontAwesomeIcon icon={faRedo} style={{ marginRight: '8px' }} />
                    Renew Pro Plan
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>

      <div
        className={`settings-subtitle-plan${
          cancelAtPeriodEnd && plan === 'pro' ? ' subtitle-canceled' : ''
        }`}
      >
        {cancelAtPeriodEnd && plan === 'pro' ? (
          <>
            Your subscription is set to end on <strong>{formattedBillingEnd}</strong>, and you’ll be moved back to the Free Plan.
          </>
        ) : (
          <>
            <strong>{subtitle}</strong> {formattedQuotaEnd}
          </>
        )}
      </div>

      <div className="plan-details-box">
        <p className="plan-intro">
          {plan === 'pro'
            ? "Thanks for subscribing to Bumpups Pro! Your Pro plan includes:"
            : "You’re on our Free Plan—here’s what you get:"}
        </p>
        <ul className="plan-benefits">
          {benefits.map((item, idx) => <li key={idx}>{item}</li>)}
        </ul>
      </div>

      {/* Purchase modal: now shown for ANY plan when showPlanPopUp is true */}
      {showPlanPopUp && (
        <PlanPurchasePage
          isOpen={true}
          onClose={() => setShowPlanPopUp(false)}
        />
      )}

      {/* Payment settings only for Pro */}
      {plan === 'pro' && <PaymentSetting />}
    </div>
  );
};

export default SelectedPlan;
